/**
 * @file    endpointBuilder.ts
 * @scope   SHARED
 * @summary Builds the Guardrails `getData` URL for the chart being previewed/configured.
 * @consumes endpoints.getData template from the global store, current chart option
 * @produces buildGetDataUrl
 * @sideEffects None.
 */

/** Inputs required to resolve a `getData` endpoint. */
export type GetDataUrlParams = {
  /** `endpoints.getData` template coming from the environment config. */
  getDataTemplate: string;
  /** Chart option currently selected (provides `chartType`, `value`, `hasMedusaQuery`). */
  currentAlias?: any;
  /** Whether the selected chart option is a child chart (rule-driven, e.g. DataGrid variants). */
  isChildChart: boolean;
  /** Whether the widget is backed by a Medusa query instead of survey/chat data. */
  hasMedusaQuery: boolean;
};

/**
 * Adapts the environment `getData` template to the current chart selection.
 *
 * The template looks like `.../GetData/{datasetGuid}/Unknown?hasMedusaQuery=false` and contains
 * two placeholders:
 * - `Unknown`            → replaced with the chart type.
 * - `UnknownBusinessRule`→ replaced with the rule name (child charts only).
 *
 * WHY: the `hasMedusaQuery` flag is flipped by replacing every `false` occurrence, mirroring the
 * original implementation so query-backed widgets keep hitting the Medusa branch of the API.
 *
 * @returns The fully resolved endpoint URL.
 */
export const buildGetDataUrl = ({
  getDataTemplate,
  currentAlias,
  isChildChart,
  hasMedusaQuery,
}: GetDataUrlParams): string => {
  let getDataUrl = '';

  if (isChildChart && !hasMedusaQuery) {
    getDataUrl = getDataTemplate
      .replace('Unknown', currentAlias?.chartType)
      .replace('UnknownBusinessRule', currentAlias?.value);
  } else if ((isChildChart && hasMedusaQuery) || hasMedusaQuery) {
    getDataUrl = getDataTemplate
      .replace('Unknown', currentAlias?.chartType)
      .replace('UnknownBusinessRule', currentAlias?.value)
      .replace(/false/gi, 'true'); // ?hasMedusaQuery
  } else {
    getDataUrl = getDataTemplate.replace('Unknown', currentAlias?.chartType);
  }

  // The chart option itself can also require the Medusa branch (enterprise chart types).
  if (currentAlias?.hasMedusaQuery) {
    getDataUrl = getDataUrl.replace(/false/gi, 'true'); // ?hasMedusaQuery
  }

  return getDataUrl;
};
